import { motion } from 'framer-motion';
import { Trophy, RotateCcw, ArrowRight, CheckCircle2, BookOpen, Sparkles } from 'lucide-react';
import { randomEncouragement } from './api';
import ShareProgressButton from './ShareProgressButton';
import FeedbackBox from './FeedbackBox';

/**
 * Écran de fin de leçon avec récap + XP + boutons.
 * Props : lessonTitle, xpGained, learnedItems (string[]), onContinue, onRestart, user
 */
export default function LessonCompleteScreen({ lessonTitle, xpGained, learnedItems = [], onContinue, onRestart, user }) {
  return (
    <motion.div initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }}
      transition={{ type: 'spring', stiffness: 180, damping: 16 }}
      className="max-w-xl mx-auto px-4 py-10" data-testid="lesson-complete">

      <motion.div initial={{ y: -20 }} animate={{ y: 0 }} transition={{ delay: 0.15 }} className="text-center mb-6">
        <div className="inline-block text-7xl mb-3" style={{ filter: 'drop-shadow(0 4px 20px rgba(250,204,21,0.4))' }}>🎉</div>
        <h2 className="text-3xl font-black bg-gradient-to-r from-yellow-300 via-emerald-300 to-yellow-200 bg-clip-text text-transparent">Bravo !</h2>
        <p className="text-white/70 text-sm mt-1">{randomEncouragement()}</p>
      </motion.div>

      <div className="bg-gradient-to-br from-emerald-900/40 to-slate-900 border border-yellow-400/30 rounded-3xl p-6 mb-5 relative overflow-hidden">
        {/* Confetti dots */}
        {[...Array(12)].map((_, i) => (
          <motion.div key={i}
            initial={{ y: -50, x: 0, opacity: 0 }}
            animate={{ y: 200, x: (i % 2 === 0 ? 1 : -1) * (50 + i * 10), opacity: [0, 1, 1, 0] }}
            transition={{ duration: 2.5, delay: i * 0.08, repeat: 0 }}
            className="absolute top-0 left-1/2 w-2 h-2 rounded-full"
            style={{ backgroundColor: ['#facc15', '#34d399', '#fbbf24', '#10b981'][i % 4] }} />
        ))}

        <div className="text-center mb-4">
          <div className="text-xs uppercase tracking-wider text-emerald-300">Leçon terminée</div>
          <div className="font-bold text-lg">{lessonTitle}</div>
        </div>

        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.4, type: 'spring' }}
          className="bg-yellow-400 text-slate-950 rounded-2xl py-4 px-5 flex items-center justify-center gap-3 mb-5">
          <Trophy size={28} />
          <div>
            <div className="text-xs uppercase font-bold tracking-wider opacity-80">XP gagné</div>
            <div className="text-3xl font-black">+{xpGained}</div>
          </div>
        </motion.div>

        {learnedItems.length > 0 && (
          <div className="bg-black/20 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2"><Sparkles size={14} className="text-yellow-300" /><div className="text-xs font-bold text-yellow-200 uppercase tracking-wider">Ce que tu as appris</div></div>
            <ul className="space-y-1.5">
              {learnedItems.slice(0, 5).map((it, i) => (
                <motion.li key={i}
                  initial={{ x: -10, opacity: 0 }} animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.6 + i * 0.1 }}
                  className="text-sm flex items-start gap-2">
                  <CheckCircle2 size={14} className="text-emerald-400 mt-0.5 flex-shrink-0" />
                  <span className="text-white/90">{it}</span>
                </motion.li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        <button onClick={onRestart} data-testid="lesson-restart"
          className="flex-1 bg-white/10 hover:bg-white/20 text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2 transition">
          <RotateCcw size={16} />Refaire la leçon
        </button>
        <button onClick={onContinue} data-testid="lesson-continue"
          className="flex-1 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-500 text-slate-950 font-bold py-3 rounded-xl flex items-center justify-center gap-2 transition shadow-lg shadow-emerald-500/30">
          Continuer<ArrowRight size={16} />
        </button>
      </div>
      {user && (
        <div className="mt-3">
          <ShareProgressButton user={user} completedLessons={1} />
        </div>
      )}

      <FeedbackBox lessonTitle={lessonTitle} />
    </motion.div>
  );
}
